import JSPDF from "jspdf";
import "jspdf-autotable";
import robotoNormal from "../../../../../assets/fonts/Roboto-Regular-normal.js"; // eslint-disable-line
import robotoBold from "../../../../../assets/fonts/Roboto-Bold-bold.js"; // eslint-disable-line
import { getFilesFromS3 } from "../../../../../utils/helpers";
import { s3AutomationPrivate } from "../../../../../utils/aws";

const PESTICIDES_HEADER_PATH = "media/batch_reports/metadata/header.png";
const SIGNATURE_PATH = "media/batch_reports/metadata/sign.png";

const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onloadend = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(blob);
});

const fetchPdfImageAssets = async () => {
  const imagePaths = [PESTICIDES_HEADER_PATH, SIGNATURE_PATH];
  const responses = await getFilesFromS3(
    imagePaths.map((path) => ({
      bucket: s3AutomationPrivate.bucket,
      region: s3AutomationPrivate.region,
      path,
    })),
    false,
    true,
    "image/png",
  );

  const failedPath = imagePaths.find((path) => !responses[path]?.success);
  if (failedPath) {
    throw new Error(`Failed to load PDF image asset from S3: ${failedPath}`);
  }

  const [pheader, signature] = await Promise.all(
    imagePaths.map((path) => blobToDataUrl(responses[path].blob)),
  );

  return { pheader, signature };
};
/**
 * Calculate the estimated height after breaking sample id into multiple lines
 * Characters limit:
 * Sample ID: 2000
 * Sample Type: 100
 * PO: 200
 * Test: 200
 */
const sampleIdTextHeight = (doc, data) => {
  const {
    sampleId, sampleType, testName, po,
  } = data;
  const combinedStTn = `${sampleType}${testName}${po}`;
  const useSampleId = sampleId.length > combinedStTn.length;
  const combinedLines = doc.splitTextToSize(combinedStTn, 140).length;
  const extraLinesMap = { // hardcoded lines
    0: 0,
    1: 0,
    2: 0,
    3: 3,
    4: 3,
  };
  if (combinedLines > 4) {
    extraLinesMap[combinedLines] = 6;
  }
  const targetString = useSampleId ? sampleId : combinedStTn;
  // We use 140 here and 90 in the header because using the same number
  // 90 produces different results.
  const sampleIdLines = doc.splitTextToSize(targetString, 140).length + (useSampleId ? 0 : extraLinesMap[combinedLines]); // if we use sample type and test name as the targetString, then we need to add 5 more lines to get more bottom space
  // If sample id lines is less than 5, no need to add any extra height to the table margin and table title
  if (sampleIdLines <= 5) {
    return 0;
  }
  // If sample id lines <= 10, use the line number + 10
  if (sampleIdLines <= 10) {
    return sampleIdLines + 10;
  }
  return (sampleIdLines - 5) * 5;
};

const header = (doc, i, pages, to, rp, rpDate, crn, rd, po, sd, st, testName, heightAdd, pheader) => {
  const arrAdd = to.split("\\n");
  const trimmedAdd = arrAdd.map((str) => str.trim());

  doc.setFontSize(9);
  doc.text("Certificate of Analysis", 171, 6);
  doc.addImage(pheader, 10, 8, 195, 25);

  const heightOne = 40;
  doc.setFont("Roboto-Bold", "bold");
  doc.text("TO:", 15, heightOne);
  doc.setFont("Roboto-Regular", "normal");
  let currentToY = 40;
  let prevSize = 1;
  trimmedAdd.forEach(
    (item) => {
      const splitText = doc.splitTextToSize(item, 100);
      prevSize = splitText.length;
      doc.text(splitText, 28, currentToY);
      currentToY += prevSize < 2 ? 5 : 8.5;
    },
  );

  doc.setFont("Roboto-Bold", "bold");

  const arrParam = ["Report #", "Customer #", "Recieved Date", "Report Date", "Page", "PO #"];
  const arrParamVal = [rp.replace(/<br>/g, ""), crn.replace(/<br>/g, ""), rd.replace(/<br>/g, ""), rpDate, `${i} of ${pages}`, po.replace(/<br>/g, "")];

  let currentInfoY = 40;
  arrParam.forEach(
    (item) => {
      doc.text(item, 130, currentInfoY);
      currentInfoY += 5;
    },
  );
  doc.setFont("Roboto-Regular", "normal");
  currentInfoY = 40;
  let poTextSize = 1;
  arrParamVal.forEach(
    (item, idx) => {
      const isPo = arrParam[idx] === "PO #";
      const textArr = isPo ? doc.splitTextToSize(item, 40) : item;
      if (isPo) {
        poTextSize = textArr.length;
      }
      doc.text(textArr, 160, currentInfoY);
      currentInfoY += 5;
    },
  );

  // Sample ID
  const heightTwo = heightOne + 30;
  doc.setFontSize(10);
  doc.setFont("Roboto-Bold", "bold");
  doc.text("Sample ID:", 15, heightTwo);
  doc.setFont("Roboto-Regular", "normal");
  const sampleID = doc.splitTextToSize(sd.replace(/<br>/g, ""), 90);
  doc.text(sampleID, 35, heightTwo, { align: "left" });

  // Sample Type
  const poHeight = (poTextSize > 1 ? (poTextSize - 1) * 4.3 - 2 : 0); // hardcoded number
  const stHeight = heightTwo + poHeight;
  doc.setFont("Roboto-Bold", "bold");
  doc.text("Sample Type:", 130, stHeight);
  doc.setFont("Roboto-Regular", "normal");
  const sampleType = doc.splitTextToSize(st.replace(/<br>/g, ""), 44);
  doc.text(sampleType, 160, stHeight, { align: "left" });

  // Test Name
  const testNameYAxis = stHeight + 5 + (sampleType.length > 1 ? (sampleType.length - 1) * 4.3 : 0); // this y axis is hardcoded
  doc.setFont("Roboto-Bold", "bold");
  doc.text("Test:", 130, testNameYAxis);
  doc.setFont("Roboto-Regular", "normal");
  const testNameArr = doc.splitTextToSize(testName, 44);
  doc.text(testNameArr, 160, testNameYAxis, { align: "left" });

  // Table Title
  // Set the Table Tile on the same page if the Sample ID length <= 1100
  if (heightAdd > -1) {
    doc.setFontSize(12);
    doc.setFont("Roboto-Bold", "bold");
    doc.text("Analytical Results (MRL - ppm)", 88, 95 + heightAdd);
    doc.setFont("Roboto-Regular", "normal");
  }

  doc.setTextColor(0);

  if (heightAdd === -1) {
    // Set the Table Tile on the next page if the Sample ID length > 1100
    doc.addPage();
    doc.setFontSize(12);
    doc.setFont("Roboto-Bold", "bold");
    doc.text("Analytical Results (MRL - ppm)", 88, 20);
    doc.setFont("Roboto-Regular", "normal");
  }
};

const footer = (doc, signature) => {
  const pageSize = doc.internal.pageSize;
  const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();

  const infoHeight = 215;
  const loqHeight = infoHeight + 10;
  const methodHeight = loqHeight + 10;
  const analysisHeight = methodHeight + 5;

  doc.setFontSize(8);
  const infoarr = doc.splitTextToSize("MRL\"s in red denote that results surpass limits established by legislation.N/A: No Applicable Legislation(No specific MRL established for the active ingredient/commodity pair). The MRL information provided above is for reference purposes only and accuracy cannot be guanrateed", 200);
  doc.text(infoarr, 14, infoHeight);
  doc.text("*LOQ is Limit of Quantitation", 14, loqHeight);
  doc.setFont("Roboto-Bold", "bold");
  doc.text("Method Reference", 14, methodHeight);
  doc.setFont("Roboto-Regular", "normal");
  doc.text("Pesticide Residue Analysis", 14, analysisHeight);
  doc.text("AOAC 2007.01", 62, analysisHeight);

  const signatureHeight = pageHeight - 40;
  const underlineHeight = signatureHeight + 14;
  const siangatureTextHeight = signatureHeight + 18;
  doc.addImage(signature, 150, signatureHeight);
  doc.line(132, underlineHeight, 190, underlineHeight, "F");
  doc.text("Laboratory Director or Approved Signatory", 134.4, siangatureTextHeight);

  doc.setFontSize(9);
  const bottominfo = doc.splitTextToSize("The results contained in this report relate only to samples submitted. Samples are tested using Genista SOPs. Genista", 190);
  const bottominfo2 = doc.splitTextToSize("Biosciences does not accept any liability for damages arising out of the use or interpretation of the results contained in this report.", 190);
  const bOneHeight = 265.4;
  const bTwoHeight = bOneHeight + 5;
  doc.text(bottominfo, 14, bOneHeight);
  doc.text(bottominfo2, 14, bTwoHeight);
};

const canMakeTextRed = (data) => {
  const row = data.row.index;
  const col = data.column.index;
  const validResult = parseFloat(data.table.body[row].raw[1]);
  const validMrl = parseFloat(data.cell.raw);
  if (!validResult || !validMrl) {
    return false;
  }
  const result = Number(data.table.body[row].raw[1]);
  const mrlVal = Number(data.cell.raw);
  if (data.cell.raw && col > 1 && result > mrlVal) {
    return true;
  }

  return false;
};

/**
 * This function will set the maximum width for each column based on the cell value'length
 * @param {*} tableData [[row1, row2, row3...]]
 * @param {*} cols [Pesticides, Result, countries....]
 */
const getColumnStyles = (tableData, cols, tableWidth) => {
  const columnStyles = {};

  // min width so that the table can stretch
  const firstColumn = 0;
  const secondColumn = 1;
  const characterWidthInPx = 1.85;
  const pesticideColWidth = 56;
  const minResultWidth = 13;
  const minMrlWidth = 11;
  const maxResultMrlWidth = (tableWidth - pesticideColWidth) / 2;

  const maxColWidth = [pesticideColWidth]; // a list of maximum widths for each column
  const updatedWidth = [pesticideColWidth, minResultWidth]; // a list of widths for each column, it will be updated by iterate through all cells
  tableData[0][0].forEach((_, i) => {
    if (i > firstColumn) {
      maxColWidth.push(maxResultMrlWidth);
    }
    if (i > secondColumn) {
      updatedWidth.push(minMrlWidth);
    }
  });

  // Get the maximum cell width for each column based on each cell width
  tableData[0].forEach((row) => {
    row.forEach((col, curColumn) => {
      if (curColumn > firstColumn) {
        const curCellWidth = (col?.length || minResultWidth) * characterWidthInPx; // calculate the estimated cell width based on the string's length
        updatedWidth[curColumn] = Math.max(curCellWidth, updatedWidth[curColumn]); // update the current column width to the maximum width
      }
    });
  });

  // After we get the maximum width for each column, if any column width > the max column width, then we set the column to the default maximum col width
  const styleKey = ["cellWidth"];
  updatedWidth.forEach((_, curColumn) => {
    if (curColumn > firstColumn) {
      // If the estimated width <= the maximum possible width, then we use the "minCellWidth" so that the cell can grow.
      // Otherwise, we need to use the "cellWidth" to wrap the text inside the cell
      styleKey.push(updatedWidth[curColumn] <= maxColWidth[curColumn] ? "minCellWidth" : "cellWidth");
      updatedWidth[curColumn] = Math.min(updatedWidth[curColumn], maxColWidth[curColumn]);
    }
  });

  // Set the final column width
  cols.forEach((_, curColumn) => {
    const style = { [styleKey[curColumn]]: updatedWidth[curColumn] };
    columnStyles[curColumn] = style;
    return true;
  });

  return columnStyles;
};

export default async function GeneratePDF(props) {
  const {
    countryCodes, table, toID, rpID, rpDate, crnID, rdID, poID, sdID, stID, testName,
  } = props;
  const { pheader, signature } = await fetchPdfImageAssets();
  const doc = new JSPDF("portrait", null, "letter", false);
  // Load and add the Roboto font
  doc.addFileToVFS("Roboto-Regular.ttf", robotoNormal); // load the base64 font data into jsPDF's virtual file system.
  doc.addFont("Roboto-Regular.ttf", "Roboto-Regular", "normal"); // tell jsPDF how to use that font internally.
  doc.addFileToVFS("Roboto-Bold.ttf", robotoBold);
  doc.addFont("Roboto-Bold.ttf", "Roboto-Bold", "bold");

  doc.setFont("Roboto-Regular", "normal");
  const tableRows = [];
  const cols = ["Pesticide", "Result", ...countryCodes];
  for (let i = 0; i < table.length; i++) {
    const insideArray = [];
    for (let j = 0; j < cols.length; j++) {
      insideArray.push(table[i][cols[j]]);
    }
    tableRows.push(insideArray);
  }
  const inside = [];
  const maxRow = tableRows.length;
  for (let i = 0; i < maxRow; i += maxRow) {
    const newI = [];
    for (let j = 0; j < maxRow; j++) {
      if ((i + j) >= tableRows.length) {
        newI.push(" ");
      } else {
        newI.push(tableRows[(i + j)]);
      }
    }
    inside.push(newI);
  }
  /** Set the min width for the Pesticide, Result and Country cell */
  const tableWidth = 190;
  const columnStyles = getColumnStyles(inside, cols, tableWidth);
  const pesticideFontSize = 9;
  const heightAdd = sampleIdTextHeight(doc, {
    sampleId: sdID, sampleType: stID, testName, po: poID,
  });
  const splitTableToSecondPage = heightAdd > 75; // If sample id has length > 1100, then the heightAdd will greater than 75
  const marginTop = splitTableToSecondPage ? 25 : 100 + heightAdd;
  const totalPagesExp = "{total_pages_count_string}";
  for (let i = 0; i < inside.length; i++) {
    doc.autoTable({
      head: [cols],
      body: inside[i].map((data) => data),
      margin: { top: marginTop, bottom: 90 },
      styles: {
        lineColor: [255, 255, 255],
        lineWidth: {
          top: 0, bottom: 2, left: 1, right: 1,
        },
      },
      headStyles: {
        fillColor: [255, 255, 255],
        fontSize: pesticideFontSize,
        textColor: [71, 41, 123],
        lineColor: [71, 41, 123],
        lineWidth: {
          top: 1, bottom: 2, left: 1, right: 1,
        },
      },
      footStyles: {
        fontSize: pesticideFontSize,
      },
      bodyStyles: {
        fillColor: [228, 217, 255],
        textColor: 0,
        fontSize: pesticideFontSize,
      },
      alternateRowStyles: {
        fillColor: [237, 249, 251],
      },
      tableWidth,
      columnStyles,
      horizontalPageBreak: true, // Break the table into separate pages if too many columns
      horizontalPageBreakRepeat: [0, 1], // Repeat the first and second column
      horizontalPageBreakBehaviour: "immediately", // Show all the columns first
      willDrawPage: () => {
        header(
          doc,
          doc.getNumberOfPages(),
          totalPagesExp,
          toID,
          rpID,
          rpDate,
          crnID,
          rdID,
          poID,
          sdID,
          stID,
          testName,
          splitTableToSecondPage ? -1 : heightAdd,
          pheader,
        );
      },
      didDrawPage: () => {
        footer(doc, signature);
      },
      didParseCell: (data) => {
        if (canMakeTextRed(data)) {
          data.cell.styles.textColor = [255, 0, 0];
        }
      },
    });
    if (typeof doc.putTotalPages === "function") {
      doc.putTotalPages(totalPagesExp);
    }
  }
  const string = doc.output("blob");
  const blob = new Blob([string], { type: "application/pdf" });
  const blobURL = URL.createObjectURL(blob);
  return { blobURL, pdf: blob };
}
